import './AsyncSearch.css';
import './../input.scss';
import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { SelectableList } from '../../SelectableList/SelectableList';
import { getProductName, Product } from '../../../model/product';
import { Event, getEventName } from '../../../model/event';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { fullUserName, User } from '../../../model/user';
import { Shortcut } from '../../Shortcut/Shortcut';
import { searchCollection, searchElementsByIds, OptionType, useRemoteSearch, Collection } from '../../../model/data';
import { handleError } from '../../../model/notification';
import { t } from '../../../locales/translate';
import { useErrorHandler } from '../../../util/util';
import { getVenueName, Venue } from '../../../model/venue';
import { Tooltip } from '../../Tooltip/Tooltip';
import * as PopperJS from 'popper.js';
import { TagsGroup } from '../../Tag/TagsGroup';
import { FormField } from '../../../model/form';
import { getCategoryName } from '../../../model/category';
import GlobalProps from '../../globalProps';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

interface Props extends FormField<string[]>, GlobalProps {
  actions?: React.ReactNode | React.ReactNodeArray;
  searchIn: OptionType[];
  required?: boolean;
  position?: PopperJS.Placement;
  maxResults?: number;
  minQueryLength?: number;
  description?: string;
  icon?: IconName;
  isLoading?: boolean;
  errorOverride?: string;
  // Flexible way of setting additional params, this can be any
  additionalParams?: JSONObject;
}

export function FormMultiSearch({
  value,
  label,
  actions,
  searchIn,
  minQueryLength = 2,
  maxResults = 10,
  icon,
  errorOverride,
  isLoading,
  description,
  position = 'bottom-start',
  inputId,
  required,
  disabled,
  errors,
  additionalParams,
  touched,
  valid,
  className,
  onChange,
}: Props): JSX.Element {
  const [selectedItems, setSelectedItems] = useState<Collection[]>([]);
  const [pendingValue, setPendingValue] = useState(' ');
  const [active, setActive] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  function getOptionLabel(
    option: Collection & {
      itemType?: string;
      sku?: string;
      email?: string;
      name?: string | null;
    }
  ): string | undefined | null {
    switch (option.itemType) {
      case 'product':
        return `${option.sku} - ${getProductName(option as Product)}`;
      case 'event':
        return getEventName(option as Partial<Event>, {
          sku: true,
          venueName: true,
          city: true,
          happeningFrom: true,
        });
      case 'user':
        return [fullUserName(option as User), option.email].filter(Boolean).join('    ');
      case 'venue':
        return getVenueName(option as Venue, { address: true });
      case 'category':
        return getCategoryName(option.id, { withShop: true }) || option.id;
      default:
        return option.name;
    }
  }

  async function getCurrentValue(): Promise<void> {
    try {
      const options = await searchElementsByIds(value as string[], searchIn);
      setSelectedItems(options || []);
    } catch (error) {
      handleError(error);
    }
  }

  useEffect(() => {
    if (value) {
      getCurrentValue();
    }
  }, []); // only when the component loads will the results be empty

  const [search, loading, error] = useRemoteSearch(
    pendingValue,
    async (term) => {
      return searchCollection({ term, types: searchIn, additionalParams });
    },
    minQueryLength
  );

  useErrorHandler(error);

  function renderOptions(): (string | undefined | null)[] {
    if (search) {
      return search.results.map((item) => getOptionLabel(item));
    }
    return [];
  }

  function removeTagAtIndex(index: number): void {
    const newSelected = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(newSelected);
    onChange?.(newSelected.map((item) => item.id));
  }

  function removeLastTag(): void {
    if (!loading) {
      const newSelected = [...selectedItems];
      newSelected.pop();
      setSelectedItems(newSelected);
      onChange?.(newSelected.map((item) => item.id));
    }
  }

  // Event Handlers
  async function handleOnChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    setPendingValue(e.currentTarget.value);
  }

  function handleOnSelect(index: number): void {
    if (!loading && search) {
      const option = search.results[index];
      if (option) {
        if (!(value || []).includes(option.id)) {
          const newSelectedItems = [...selectedItems, option];
          setSelectedItems(newSelectedItems);
          onChange?.([...(value || []), option.id]);
          setPendingValue(' ');
        }
      } else {
        onChange?.(selectedItems.map((item) => item.id));
        setActive(false);
      }
    }
  }

  function handleOnClear(): void {
    setPendingValue('');
    setSelectedItems([]);
    setActive(false);
    onChange?.(undefined);
  }

  function renderInput(): JSX.Element {
    return (
      <div className="async-search__input-container">
        <input
          className="input__input"
          type="text"
          id={inputId}
          value={pendingValue}
          autoComplete="off"
          onChange={handleOnChange}
          disabled={disabled}
        />
        <Icon
          className={classnames('async-search__loading', {
            'async-search__loading--active': isLoading || loading,
          })}
          name="search--sm"
          loading={isLoading || loading}
        />
      </div>
    );
  }

  const displayErrors = touched && !valid;

  return (
    <div className={classnames('input', 'async-search', className)} ref={parentRef}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': (value !== undefined && value !== null) || active,
        })}
      >
        <Tooltip
          style={{ width: parentRef.current?.clientWidth }}
          open={!disabled && active}
          onChange={(open) => {
            if (!disabled) {
              setActive(open);
            }
          }}
          rounded={false}
          gap="none"
          arrow={false}
          position={position}
          mode="click"
          reference={
            <div className="input__input-wrapper">
              {label && (
                <label className="input__label" htmlFor={inputId}>
                  {label}
                  {!required && ` (${t.general.optional})`}
                </label>
              )}
              <TagsGroup
                tags={selectedItems.map((item) => ({
                  id: item.id,
                  label: getOptionLabel(item),
                }))}
                input={renderInput()}
                actions={<Shortcut keys="Backspace" onInvoke={removeLastTag} overrideInput={active} />}
                onRemove={removeTagAtIndex}
                onClear={handleOnClear}
                expandHiddenTags="bottom-end"
              />
              {actions && <div className="input__actions">{actions}</div>}
            </div>
          }
        >
          <SelectableList
            className="async-search__select-menu-items"
            onSelect={handleOnSelect}
            unSelectableFooter={
              search && search.total > maxResults ? (
                <span className="async-search__more-results">{t.errors.moreResults(search.total)}</span>
              ) : undefined
            }
            options={renderOptions()}
          />
        </Tooltip>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          {errorOverride ? (
            <div className="input__error-message">{errorOverride}</div>
          ) : (
            <Collapse open={displayErrors}>
              {errors?.map((error) => {
                return (
                  <div className="input__error-message" key={error}>
                    {t.errors[error] || error}
                  </div>
                );
              })}
            </Collapse>
          )}
        </div>
      </div>
    </div>
  );
}
